/**
 * The Civic Pass (screenshots/92–105): a badge that mirrors sign-up and onboarding. Pure: the form is the
 * source of truth, the pass is derived from it, and a polite live region announces what changed.
 *
 * Six segments: name · email verified · who you are · where (ward / office / org) · what you follow · issued.
 * They are the only progress indicator (no stepper); once issued they merge into one solid line.
 * Each group has its own pass: resident (white, ward watermark), office (ink, Chicago-blue strap with red
 * stars), advocacy (plum, team initials). What you follow becomes enamel pins on the strap and the pass.
 */
import type { UserGroup } from "../nav/navConfig";

export interface PassInput {
  name: string;
  email: string;
  emailVerified: boolean;
  group: UserGroup | null;
  /** Residents and offices: the ward. Advocacy: the organization. */
  ward?: { number: number; neighborhood?: string; alderpersonName: string } | null;
  orgName?: string | null;
  orgStatus?: "member" | "requested" | null;
  topics: string[];
  follows: { recordId: string; status?: "pending" | "passed" | "failed" }[];
  issuedAt?: string | null;          // ISO date once onboarding finishes
  userId?: string;                   // for the pass number
  invitedBy?: string | null;         // "Ward 32 office · invited by Maria"
  /** Shown in the Email field instead of the address while the form rejects it ("needs a City email", 97). */
  emailNote?: string | null;
}

export type PassTheme = "neutral" | UserGroup;
export interface Pin { id: string; kind: "topic" | "bill"; label: string; icon: string; color: string }

export interface PassView {
  theme: PassTheme;
  kind: string;                      // "RESIDENT PASS"
  name: string | null;               // null = ghost "Your name"
  initials: string | null;
  role: string | null;
  fields: { label: string; value: string | null; error?: boolean }[];
  pins: Pin[];                       // all of them (strap shows up to 8, the pass up to 4 + "+n")
  watermark: string | null;          // ward number or team initials
  filled: number;                    // 0–6
  status: string;                    // "3 OF 6" or "ISSUED OCT 8, 2026"
  number: string;                    // "Nº 0060624" once issued, else "Nº 0000000"
  issued: boolean;
}

export const SEGMENTS = 6;
export const STRAP_PINS = 8, PASS_PINS = 4;

const KIND: Record<PassTheme, string> = { neutral: "CIVIC PASS", resident: "RESIDENT PASS", office: "✶ OFFICE PASS", advocacy: "ADVOCACY PASS" };
const ROLE: Record<UserGroup, string> = { resident: "Resident", office: "Alderperson’s office", advocacy: "Advocacy" };

/** Topic → enamel pin. Unknown topics get a neutral tag pin. */
export const TOPIC_PINS: Record<string, { icon: string; color: string }> = {
  "Housing": { icon: "house", color: "#E3342F" }, "Renters’ rights": { icon: "key-round", color: "#2B5F9E" },
  "Street vending": { icon: "utensils", color: "#E59A2F" }, "Parking": { icon: "square-parking", color: "#1F8F7F" },
  "Public safety": { icon: "shield", color: "#3C2A4D" }, "Budget & taxes": { icon: "receipt", color: "#6D4C7D" },
  "Schools": { icon: "school", color: "#2B5F9E" }, "Transit": { icon: "train-front", color: "#1F8F7F" },
  "Immigration": { icon: "globe", color: "#C08497" }, "Environment": { icon: "leaf", color: "#2E8B57" },
  "Small business": { icon: "store", color: "#E59A2F" }, "Zoning": { icon: "map", color: "#6E6474" },
};
/** Bill pins change color when the bill moves. */
export const BILL_PIN_COLOR = { pending: "#1A1220", passed: "#1F8F7F", failed: "#C8442B" } as const;

/** The pin for a topic label. Our topic labels (account/logic.ts TOPICS) spell "Renters' rights" with a straight apostrophe. */
export const topicPin = (t: string) => TOPIC_PINS[t] ?? TOPIC_PINS[t.replace(/'/g, "’")] ?? { icon: "tag", color: "#6E6474" };

export function pinsFor(topics: string[], follows: PassInput["follows"]): Pin[] {
  return [
    ...topics.map((t) => ({ id: `topic:${t}`, kind: "topic" as const, label: t, ...topicPin(t) })),
    ...follows.map((f) => ({ id: `bill:${f.recordId}`, kind: "bill" as const, label: f.recordId, icon: "scroll-text", color: BILL_PIN_COLOR[f.status ?? "pending"] })),
  ];
}

export function initialsOf(name: string): string | null {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** "Byron Sigcho-López" → "B. Sigcho-López", so it fits the field. */
export const shortName = (full: string) => { const p = full.trim().split(/\s+/); return p.length > 1 ? `${p[0][0]}. ${p.slice(1).join(" ")}` : full; };

/** Stable 7-digit pass number from the user id (FNV-1a), so it never changes between sessions. */
export function passNumber(userId: string): string {
  let h = 0x811c9dc5;
  for (const ch of userId) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; }
  return `Nº ${String(h % 10_000_000).padStart(7, "0")}`;
}

const teamInitials = (org: string) => org.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).map((w) => w[0]).join("").slice(0, 3) || null;

const emailField = (p: PassInput) => (p.emailNote ? { label: "Email", value: p.emailNote, error: true } : { label: "Email", value: p.email || null });

export function passView(p: PassInput): PassView {
  const name = p.name.trim() || null;
  const theme: PassTheme = p.group ?? "neutral";
  const where = p.group === "advocacy" ? !!p.orgName : !!p.ward;
  const pins = pinsFor(p.topics, p.follows);
  const steps = [!!name, p.emailVerified, !!p.group, where, pins.length > 0, !!p.issuedAt];
  const filled = steps.filter(Boolean).length;
  const issued = !!p.issuedAt;
  const fields = p.group === "advocacy"
    ? [{ label: "Team", value: p.orgName ?? null }, { label: "Status", value: p.orgName ? (p.orgStatus === "member" ? "Member" : "Asked to join") : null },
       { label: "Following", value: null }, emailField(p)]
    : [{ label: "Ward", value: p.ward ? `${p.ward.number}${p.ward.neighborhood ? ` · ${p.ward.neighborhood}` : ""}` : null },
       { label: "Alderperson", value: p.ward ? shortName(p.ward.alderpersonName) : null },
       { label: "Following", value: null },                            // shown as pins, never a count
       emailField(p)];
  return {
    theme, kind: KIND[theme], name, initials: name ? initialsOf(name) : null,
    role: p.group ? (p.invitedBy ?? ROLE[p.group]) : null,
    fields, pins,
    watermark: p.group === "advocacy" ? (p.orgName ? teamInitials(p.orgName) : null) : p.ward ? String(p.ward.number) : null,
    filled,
    status: issued ? `ISSUED ${new Date(p.issuedAt!).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/Chicago" }).toUpperCase()}` : `${filled} OF ${SEGMENTS}`,
    number: issued && p.userId ? passNumber(p.userId) : "Nº 0000000",
    issued,
  };
}

/** What the live region says when the pass changes. Empty string = say nothing. */
export function announce(prev: PassView, next: PassView): string {
  if (next.issued && !prev.issued) return "Your Civic Pass is issued.";
  const changed: string[] = [];
  if (next.name !== prev.name && next.name) changed.push(`name ${next.name}`);
  if (next.role !== prev.role && next.role) changed.push(next.role);
  for (const f of next.fields) {
    const before = prev.fields.find((x) => x.label === f.label)?.value;
    if (f.value && f.value !== before && f.label !== "Email") changed.push(`${f.label.toLowerCase()} ${f.value}`);
  }
  const added = next.pins.filter((x) => !prev.pins.some((y) => y.id === x.id));
  const removed = prev.pins.filter((x) => !next.pins.some((y) => y.id === x.id));
  if (added.length) changed.push(`pinned ${added.map((x) => x.label).join(", ")}`);
  if (removed.length) changed.push(`unpinned ${removed.map((x) => x.label).join(", ")}`);
  return changed.length ? `Pass updated: ${changed.join(", ")}.` : "";
}

/** Pins newly added between two views (they fly onto the strap). */
export const newPins = (prev: PassView, next: PassView) => next.pins.filter((x) => !prev.pins.some((y) => y.id === x.id));
